// Produces the portable copy of a recording, encoded as it is captured.
//
// The pipeline still gets webm/opus from MediaRecorder — native, battle-tested, and better for
// Whisper at the same bitrate. This runs alongside purely so the lawyer's own copy opens on a
// phone, in Windows Media Player, or anywhere else webm does not. If any part of it fails the
// recording is unaffected; the download simply falls back to the webm.

export const MP3_BITRATE_KBPS = 96;

// What LAME can actually be told to encode: MPEG1, MPEG2 and MPEG2.5 layer III rates.
const SUPPORTED_SAMPLE_RATES = [8000, 11025, 12000, 16000, 22050, 24000, 32000, 44100, 48000];

export const mp3RateSupported = (rate) => SUPPORTED_SAMPLE_RATES.includes(Math.round(rate));

export async function startMp3Capture(ctx, sourceNode) {
  const sampleRate = Math.round(ctx.sampleRate);
  if (!mp3RateSupported(sampleRate)) {
    console.warn(`MP3 copy skipped: ${sampleRate} Hz is not an MP3 sample rate`);
    return null;
  }

  let worker;
  try {
    worker = new Worker(new URL("./mp3EncoderWorker.js", import.meta.url));
  } catch (err) {
    console.warn("MP3 copy skipped: encoder worker unavailable", err);
    return null;
  }

  let broken = false;
  worker.onmessage = (event) => {
    if (event.data && event.data.type === "error") {
      broken = true;
      console.warn("MP3 encoder failed mid-recording", event.data.message);
    }
  };
  worker.postMessage({ type: "start", sampleRate, bitrate: MP3_BITRATE_KBPS });

  const sendPcm = (mono) => {
    if (broken) return;
    worker.postMessage({ type: "pcm", samples: mono }, [mono.buffer]);
  };

  let tap = null;
  if (ctx.audioWorklet) {
    try {
      await ctx.audioWorklet.addModule("/mp3-tap-worklet.js");
      tap = new AudioWorkletNode(ctx, "mp3-tap");
      tap.port.onmessage = (event) => sendPcm(event.data);
    } catch (err) {
      console.warn("AudioWorklet unavailable, falling back to ScriptProcessor", err);
      tap = null;
    }
  }

  if (!tap) {
    // Deprecated, but it exists in every browser that lacks AudioWorklet.
    tap = ctx.createScriptProcessor(4096, 2, 1);
    tap.onaudioprocess = (event) => {
      const buffer = event.inputBuffer;
      const left = buffer.getChannelData(0);
      const right = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : null;
      const mono = new Float32Array(left.length);
      if (right) {
        for (let i = 0; i < left.length; i += 1) mono[i] = (left[i] + right[i]) / 2;
      } else {
        mono.set(left);
      }
      sendPcm(mono);
    };
  }

  // Both node types only run while the graph pulls them, and the graph only pulls what reaches a
  // destination — so the tap is routed to the speakers through a gain pinned at zero. Without the
  // mute the hearing would play back out loud and feed straight into the microphone.
  const mute = ctx.createGain();
  mute.gain.value = 0;
  sourceNode.connect(tap);
  tap.connect(mute);
  mute.connect(ctx.destination);

  const teardown = () => {
    try { sourceNode.disconnect(tap); } catch (err) { /* already torn down */ }
    try { tap.disconnect(); } catch (err) { /* already torn down */ }
    try { mute.disconnect(); } catch (err) { /* already torn down */ }
    if (tap.port) tap.port.onmessage = null;
    tap.onaudioprocess = null;
  };

  return {
    async stop() {
      teardown();
      if (broken) {
        worker.terminate();
        return null;
      }
      return new Promise((resolve) => {
        // Flushing a long recording still has to finish the last frames; if it stalls, the webm
        // download is right there, so waiting forever would be the worse failure.
        const giveUp = setTimeout(() => {
          worker.terminate();
          resolve(null);
        }, 30000);

        worker.onmessage = (event) => {
          const data = event.data || {};
          if (data.type === "done") {
            clearTimeout(giveUp);
            worker.terminate();
            resolve(data.blob && data.blob.size > 0 ? data.blob : null);
          } else if (data.type === "error") {
            clearTimeout(giveUp);
            worker.terminate();
            resolve(null);
          }
        };
        worker.postMessage({ type: "finish" });
      });
    },
    abort() {
      teardown();
      worker.terminate();
    },
  };
}
